import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import apiClient from '../api/apiClient';
import useAuth from '../hooks/useAuth';
import Logo from '../components/common/Logo';

const Register = () => {
    const { token } = useParams();
    const { register, user } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
    });
    const [loading, setLoading] = useState(true);
    const [validating, setValidating] = useState(true);
    const [error, setError] = useState('');
    const [invalidMessage, setInvalidMessage] = useState('');

    const { name, email, password, confirmPassword } = formData;

    // Rediriger si déjà connecté
    useEffect(() => {
        if (user) navigate('/');
    }, [user, navigate]);

    // Valider le token au chargement
    useEffect(() => {
        const validateToken = async () => {
            try {
                const response = await apiClient.get(`/invitations/validate/${token}`);
                if (response.data.valid) {
                    setFormData((prev) => ({ ...prev, email: response.data.email }));
                    setValidating(false);
                }
            } catch (err) {
                const message = err.response?.data?.message || 'Lien d\'inscription invalide.';
                setInvalidMessage(message);
                setValidating(false);
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            validateToken();
        } else {
            setInvalidMessage('Aucun lien d\'inscription fourni.');
            setValidating(false);
            setLoading(false);
        }
    }, [token]);

    const onChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Les mots de passe ne correspondent pas.');
            return;
        }

        if (password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères.');
            return;
        }

        setLoading(true);
        try {
            await register({ name, email, password }, token);
        } catch (err) {
            const message = err.response?.data?.message || 'Échec de l\'inscription. Veuillez réessayer.';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    // Écran de chargement
    if (loading && validating) {
        return (
            <div className="auth-container page-transition">
                <div className="auth-card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#9203;</div>
                    <h1 className="auth-title">Vérification en cours...</h1>
                    <p className="auth-subtitle">Validation de votre lien d'inscription.</p>
                </div>
            </div>
        );
    }

    // Lien invalide / expiré / déjà utilisé
    if (invalidMessage) {
        return (
            <div className="auth-container page-transition">
                <div className="auth-card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10060;</div>
                    <h1 className="auth-title">Lien invalide</h1>
                    <p className="auth-subtitle" style={{ marginBottom: '24px' }}>{invalidMessage}</p>
                    <Link
                        to="/login"
                        className="btn btn-primary"
                        style={{ width: '100%', padding: '15px', display: 'block', textAlign: 'center', textDecoration: 'none' }}
                    >
                        Retour à la connexion
                    </Link>
                </div>
            </div>
        );
    }

    // Formulaire d'inscription
    return (
        <div className="auth-container page-transition">
            <div className="auth-card">
                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                        <Logo size="large" showText={false} variant="auth" />
                    </div>
                    <p className="auth-subtitle">Créez votre compte - by OumanourCreations</p>
                </div>

                {error && (
                    <div style={{
                        background: 'var(--urgent, #E63946)',
                        color: 'white',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        fontSize: '14px'
                    }}>
                        {error}
                    </div>
                )}

                <form onSubmit={onSubmit}>
                    <div className="input-group">
                        <label className="input-label" htmlFor="name">Nom complet</label>
                        <input
                            type="text"
                            className="input-field"
                            id="name"
                            name="name"
                            value={name}
                            placeholder="Entrez votre nom"
                            onChange={onChange}
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label" htmlFor="email">Email</label>
                        <input
                            type="email"
                            className="input-field"
                            id="email"
                            name="email"
                            value={email}
                            readOnly
                            style={{ backgroundColor: 'var(--bg-secondary, #f0f0f0)', cursor: 'not-allowed' }}
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label" htmlFor="password">Mot de passe</label>
                        <input
                            type="password"
                            className="input-field"
                            id="password"
                            name="password"
                            value={password}
                            placeholder="Min. 8 caractères, 1 majuscule, 1 chiffre"
                            onChange={onChange}
                            required
                        />
                    </div>

                    <div className="input-group" style={{ marginBottom: '30px' }}>
                        <label className="input-label" htmlFor="confirmPassword">Confirmer le mot de passe</label>
                        <input
                            type="password"
                            className="input-field"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={confirmPassword}
                            placeholder="Confirmez le mot de passe"
                            onChange={onChange}
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary"
                        style={{ width: '100%', padding: '15px' }}
                        disabled={loading}
                    >
                        {loading ? 'Inscription en cours...' : 'S\'inscrire'}
                    </button>
                </form>

                <div className="auth-footer" style={{ textAlign: 'center', marginTop: '16px' }}>
                    <p>
                        Vous avez déjà un compte ? <Link to="/login" style={{ color: 'var(--primary)', textDecoration: 'none' }}>Se connecter</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Register;
